import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from "@exhousing/design-system";
import { Pencil } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { ContactFieldsEditor } from "@/components/party/ContactFieldsEditor";
import { useUpdateGuarantor } from "@/hooks/api/useParties";
import { CONTACT_TEXT_FIELDS, buildContactPayload } from "@/lib/contactFields";
import type { components } from "@/types/api";

type Guarantor = components["schemas"]["GuarantorResponse"];

export function GuarantorDetailsCard({ guarantor }: { guarantor: Guarantor }) {
  const update = useUpdateGuarantor(guarantor.id);
  const [editing, setEditing] = useState(false);
  const c = guarantor.contact as Record<string, unknown>;

  const [contact, setContact] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {
      title: (c.title as string) ?? "",
      gender: (c.gender as string) ?? "",
      language: (c.language as string) ?? "nl",
    };
    for (const { key } of CONTACT_TEXT_FIELDS) init[key] = (c[key] as string) ?? "";
    return init;
  });
  const [form, setForm] = useState({
    income_type: guarantor.income_type ?? "",
    gross_monthly_income: guarantor.gross_monthly_income?.toString() ?? "",
    iban: guarantor.iban ?? "",
    bank_account_name: guarantor.bank_account_name ?? "",
    relationship_to_tenant: guarantor.relationship_to_tenant ?? "",
  });
  const [isActive, setIsActive] = useState(guarantor.is_active);

  const setC = (k: string, v: string) => setContact((s) => ({ ...s, [k]: v }));

  const save = (e: React.FormEvent) => {
    e.preventDefault();
    update.mutate(
      {
        contact: buildContactPayload(contact, "person"),
        income_type: form.income_type || null,
        gross_monthly_income: form.gross_monthly_income
          ? Number(form.gross_monthly_income)
          : null,
        iban: form.iban || null,
        bank_account_name: form.bank_account_name || null,
        relationship_to_tenant: form.relationship_to_tenant || null,
        is_active: isActive,
      },
      {
        onSuccess: () => {
          toast.success("Guarantor updated");
          setEditing(false);
        },
        onError: () => toast.error("Could not update guarantor"),
      },
    );
  };

  if (editing) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Edit guarantor</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={save} className="space-y-5">
            <ContactFieldsEditor form={contact} set={setC} />
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="income_type">Income type</Label>
                <Input
                  id="income_type"
                  value={form.income_type}
                  onChange={(e) => setForm({ ...form, income_type: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="gross_monthly_income">Gross monthly income</Label>
                <Input
                  id="gross_monthly_income"
                  type="number"
                  value={form.gross_monthly_income}
                  onChange={(e) =>
                    setForm({ ...form, gross_monthly_income: e.target.value })
                  }
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="relationship_to_tenant">Relationship to tenant</Label>
                <Input
                  id="relationship_to_tenant"
                  value={form.relationship_to_tenant}
                  onChange={(e) =>
                    setForm({ ...form, relationship_to_tenant: e.target.value })
                  }
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="iban">IBAN</Label>
                <Input
                  id="iban"
                  value={form.iban}
                  onChange={(e) => setForm({ ...form, iban: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="bank_account_name">Account holder</Label>
                <Input
                  id="bank_account_name"
                  value={form.bank_account_name}
                  onChange={(e) =>
                    setForm({ ...form, bank_account_name: e.target.value })
                  }
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-4">
              <label className="flex cursor-pointer items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  className="size-4 rounded border-input accent-primary"
                />
                Active
              </label>
            </div>
            <div className="flex gap-2">
              <Button type="submit" size="sm" disabled={update.isPending}>
                {update.isPending ? "Saving…" : "Save guarantor"}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => setEditing(false)}
              >
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    );
  }

  const row = (label: string, value: unknown) => (
    <div>
      <dt className="text-muted-foreground">{label}</dt>
      <dd>{value ? String(value) : "—"}</dd>
    </div>
  );

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Details</CardTitle>
        <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
          <Pencil className="size-4" />
          Edit
        </Button>
      </CardHeader>
      <CardContent>
        <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
          {row("Email", guarantor.contact.email)}
          {row("Phone", guarantor.contact.phone ?? guarantor.contact.mobile)}
          {row("Income type", guarantor.income_type)}
          {row(
            "Gross monthly income",
            guarantor.gross_monthly_income != null
              ? `€ ${Number(guarantor.gross_monthly_income).toLocaleString("nl-NL")}`
              : null,
          )}
          {row("Relationship to tenant", guarantor.relationship_to_tenant)}
          {row("IBAN", guarantor.iban)}
          {row("Account holder", guarantor.bank_account_name)}
        </dl>
      </CardContent>
    </Card>
  );
}
